import Input from "../../../../components/Input/Input";
import { ProjectsAvailableSessionTypes, TypeNewProject } from "../../../../types/Projects";
import s from "../ProjectFormPage.module.css";
import { ProjectFormController } from "../useProjectFormController";

type RulesStepProps = {
  controller: ProjectFormController;
};

type NumericRuleField =
  | "clientCancelThreshold"
  | "clientRescheduleThreshold"
  | "specialistCancelThreshold"
  | "clientCancelPackageThreshold"
  | "clientReschedulePackageAmount"
  | "clientReschedulePackageThreshold";

const setOptionalInteger = (
  controller: ProjectFormController,
  field: NumericRuleField,
  value: string,
) => {
  const numericValue = value === "" ? undefined : Number(value);

  if (
    numericValue === undefined ||
    (Number.isInteger(numericValue) && numericValue >= 0)
  ) {
    controller.setProjectData((prev) => ({
      ...prev,
      [field]: numericValue,
    }));
  }
};

const getRuleValue = (
  projectData: TypeNewProject,
  field: NumericRuleField,
) => projectData[field]?.toString() ?? "";

const RuleInput = ({
  controller,
  field,
  label,
  ariaLabel,
}: {
  controller: ProjectFormController;
  field: NumericRuleField;
  label: string;
  ariaLabel: string;
}) => (
  <div className={s.fieldBlock}>
    <label className={s.fieldLabel}>{label}</label>
    <Input
      inputValue={getRuleValue(controller.projectData, field)}
      onChangeInput={(value) => setOptionalInteger(controller, field, value)}
      isVisible
      required={false}
      type="number"
      minNumber={0}
      maxNumber={48}
      ariaLabel={ariaLabel}
    />
  </div>
);

const RulesStep = ({ controller }: RulesStepProps) => {
  const hasSessionRules = controller.selectedProjectTypes.includes(
    ProjectsAvailableSessionTypes.SESSION,
  );
  const hasPackageRules = controller.selectedProjectTypes.includes(
    ProjectsAvailableSessionTypes.PACKAGE,
  );

  if (!controller.selectedProjectTypes.length) {
    return (
      <div className={s.stepPanel}>
        <div className={s.section}>
          <h2 className={s.sectionTitle}>Rules</h2>
          <p className={s.sectionText}>
            Select a Project type before configuring cancellation and reschedule
            rules.
          </p>
        </div>
      </div>
    );
  }

  if (controller.projectData.isBarter) {
    return (
      <div className={s.stepPanel}>
        <div className={s.section}>
          <h2 className={s.sectionTitle}>Rules</h2>
          <p className={s.sectionText}>
            Rules are not required for barter Projects.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={s.stepPanel}>
      <div className={s.section}>
        <h2 className={s.sectionTitle}>Rules</h2>
        <p className={s.sectionText}>
          Configure cancellation and reschedule thresholds for enabled Project
          types.
        </p>
      </div>

      {hasSessionRules && (
        <div className={s.ruleGroup}>
          <h3 className={s.groupTitle}>Session rules</h3>
          <div className={s.fieldGrid}>
            <RuleInput
              controller={controller}
              field="clientCancelThreshold"
              label="Cancellation of one session at the client's initiative (hours before the start) number from 1 to 48."
              ariaLabel="Client session cancellation threshold"
            />
            <RuleInput
              controller={controller}
              field="clientRescheduleThreshold"
              label="Reschedule one session at the client's initiative (hours before the start) number from 1 to 48."
              ariaLabel="Client session reschedule threshold"
            />
            <RuleInput
              controller={controller}
              field="specialistCancelThreshold"
              label="Cancellation of one session at the initiative of the expert (hours before the start) number from 1 to 48."
              ariaLabel="Specialist session cancellation threshold"
            />
          </div>
        </div>
      )}

      {hasPackageRules && (
        <div className={s.ruleGroup}>
          <h3 className={s.groupTitle}>Package rules</h3>
          <div className={s.fieldGrid}>
            <RuleInput
              controller={controller}
              field="clientCancelPackageThreshold"
              label="Client's termination of contract for session package (hours before the start of the next session) number from 1 to 48."
              ariaLabel="Client package cancellation threshold"
            />
            <RuleInput
              controller={controller}
              field="clientReschedulePackageAmount"
              label="The number of sessions transferred by the client within the session package (for 5 sessions) is a number from 1 to 5."
              ariaLabel="Client package reschedule amount"
            />
            <RuleInput
              controller={controller}
              field="clientReschedulePackageThreshold"
              label="Transfer of sessions from the package at the client's initiative (several hours before the start) number from 1 to 48."
              ariaLabel="Client package reschedule threshold"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default RulesStep;
